import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useOutletContext } from "react-router-dom";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import {
  checkPlatformWorkspaceSlugAvailability,
  createPlatformWorkspace,
  getPlatformWorkspacePresentationStatus,
  getWorkspaceCreatePlanCatalog,
  listAdminResellers,
  listPlatformWorkspaceSegmentCatalog,
  listPlatformWorkspaceModelTemplates,
} from "../../../api/client";
import WorkspaceCreateWizard from "../../../components/workspaces/WorkspaceCreateWizard";
import { PLATFORM_CONSOLE_PATH, workspaceDetailPath } from "./platformConsoleRoutes";

function Section({ children }) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.secondary.main, 0.04),
        borderColor: "divider",
        p: 3,
      })}
    >
      {children}
    </Paper>
  );
}

const PlatformWorkspaceCreatePage = () => {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const outlet = useOutletContext() || {};
  const [planItems, setPlanItems] = useState([]);
  const [resellerItems, setResellerItems] = useState([]);
  const [segmentItems, setSegmentItems] = useState([]);
  const [modelTemplateItems, setModelTemplateItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const locale = i18n.resolvedLanguage || i18n.language || "pt-BR";
    Promise.all([
      getWorkspaceCreatePlanCatalog().catch(() => ({ items: [] })),
      listAdminResellers().catch(() => ({ items: [] })),
      listPlatformWorkspaceSegmentCatalog({ locale }).catch(() => ({ items: [] })),
      listPlatformWorkspaceModelTemplates({ status: "published" }).catch(() => ({ items: [] })),
    ]).then(([plans, resellers, segs, models]) => {
      if (cancelled) return;
      setPlanItems(Array.isArray(plans?.items) ? plans.items : []);
      setResellerItems(Array.isArray(resellers?.items) ? resellers.items : []);
      setSegmentItems(Array.isArray(segs?.items) ? segs.items : []);
      setModelTemplateItems(Array.isArray(models?.items) ? models.items : []);
    });
    if (typeof outlet.stopRefreshing === "function") outlet.stopRefreshing();
    return () => {
      cancelled = true;
    };
  }, [outlet, i18n.resolvedLanguage, i18n.language]);

  const handleCompleted = useCallback(
    (result) => {
      if (result?.dominioId) {
        navigate(workspaceDetailPath(result.dominioId));
      }
    },
    [navigate]
  );

  return (
    <Section>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <Button
          component={RouterLink}
          to={PLATFORM_CONSOLE_PATH.workspaces}
          startIcon={<ArrowBackIcon />}
          size="small"
          variant="outlined"
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
        >
          {t("workspaceCreate.page.backToList")}
        </Button>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 0.75, fontWeight: 700, letterSpacing: -0.02 }}>
            {t("workspaceCreate.page.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.55 }}>
            {t("workspaceCreate.page.subtitle")}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <WorkspaceCreateWizard
            checkSlugAvailability={checkPlatformWorkspaceSlugAvailability}
            createWorkspace={createPlatformWorkspace}
            getPresentationStatus={getPlatformWorkspacePresentationStatus}
            planCatalogItems={planItems}
            showClassificationFields
            resellerItems={resellerItems}
            segmentItems={segmentItems}
            modelTemplateItems={modelTemplateItems}
            showTemplatePicker
            onCompleted={handleCompleted}
            openWorkspaceUrl={(res) => (res?.dominioId ? workspaceDetailPath(res.dominioId) : null)}
          />
        </Box>
      </Stack>
    </Section>
  );
};

export default PlatformWorkspaceCreatePage;
